import { type SupportReach } from '../hooks/useSupportChat';
import { type ChatCounterpart, DESK_COUNTERPART } from '../interface/support-chat';
import { firstNameOf } from './chat-counterpart-copy';

export interface ISupportReachNotice {
	message: string;
	/** Only offered where trying again can actually change the outcome. */
	canRetry: boolean;
}

/**
 * What to tell an agent when support cannot be reached.
 *
 * <p>Returns null for the two states that are not worth a banner: `unknown` (nothing has been
 * attempted yet) and `ok`. A banner on every screen is a banner nobody reads.</p>
 *
 * <p>Every message ends by reassuring the agent their text is kept. That is the fact that
 * decides what they do next — an agent who thinks a report was lost re-types it or gives up
 * on the app and uses the radio.</p>
 */
export const supportReachNotice = (
	reach: SupportReach,
	counterpart: ChatCounterpart = DESK_COUNTERPART
): ISupportReachNotice | null => {
	if (counterpart.kind === 'peer') return peerReachNotice(reach, counterpart);

	switch (reach) {
		case 'unavailable':
			return {
				message: "Support chat isn't switched on for this server yet. Your messages are saved and will send once it is.",
				canRetry: true,
			};
		case 'forbidden':
			return {
				message:
					"This account can't open a support conversation. Ask your party admin to check your agent assignment. Your messages are saved.",
				canRetry: false,
			};
		case 'signedOut':
			return { message: 'Your session expired. Sign in again — your messages are saved.', canRetry: false };
		case 'failed':
			return { message: "Can't reach the support desk right now. Your messages are saved and will retry.", canRetry: true };
		default:
			return null;
	}
};

/**
 * The same four answers for an agent↔agent conversation.
 *
 * <p>`forbidden` reads differently here: on the support thread it means a claim-poor token, on
 * a peer thread it means "this person is not your parent or child" — which is what happens
 * after a reassignment. It is not retryable; the roster has to change first.</p>
 */
const peerReachNotice = (reach: SupportReach, counterpart: ChatCounterpart): ISupportReachNotice | null => {
	switch (reach) {
		case 'unavailable':
			return {
				message: "Agent-to-agent chat isn't switched on for this server yet. Your messages are saved and will send once it is.",
				canRetry: true,
			};
		case 'forbidden':
			return {
				message:
					"This conversation isn't available to your account. You can only message agents you supervise or who supervise you. Your draft is kept on this phone.",
				canRetry: false,
			};
		case 'signedOut':
			return { message: 'Your session expired. Sign in again — your messages are saved.', canRetry: false };
		case 'failed':
			return {
				message: `Can't reach ${firstNameOf(counterpart.name)} right now. Your messages are saved and will retry.`,
				canRetry: true,
			};
		default:
			return null;
	}
};
